import { DashboardCard } from '@/components/ui/dashboard-card.tsx';
import { cn } from '@/utils/utils.ts';
import { EnergyManagementChart } from '@/components/energy-management-chart.tsx';
import { DateRangePicker } from '@/components/ui/date-range-picker.tsx';
import type { RangeGranularity } from '@/utils/granularity.ts';
import { useState, useMemo } from 'react';
import { useGetPlantEnergyManagementChartQuery } from '@/features/plants/hooks/queries/[plantId]/use-get-plant-energy-management-chart.ts';
import { useMetricPermissions } from '@/hooks/use-metric-permissions.ts';
import type { MetricKey } from '@/components/energy-management-chart.tsx';

export const PlantStorageOnlyEnergyManagement = ({
  className,
  plantId,
  isLoading,
}: {
  className?: string;
  plantId: string | undefined;
  isLoading?: boolean;
}) => {
  const permissionHiddenMetrics = useMetricPermissions();

  const hiddenMetrics = useMemo(() => {
    const set = new Set<MetricKey>(['pv_production']);
    for (const m of permissionHiddenMetrics) set.add(m);
    return [...set];
  }, [permissionHiddenMetrics]);

  const [dateRange, setDateRange] = useState<{
    startDate: Date;
    endDate: Date | undefined;
    rangeGranularity: RangeGranularity;
  }>({
    startDate: new Date(new Date().setHours(0, 0, 0, 0)),
    endDate: new Date(new Date().setHours(23, 59, 59, 999)),
    rangeGranularity: '5min',
  });

  const { data, isFetching } = useGetPlantEnergyManagementChartQuery({
    plantId,
    dateRange,
    enabled: Boolean(plantId),
  });

  return (
    <DashboardCard
      className={cn(
        `w-full gap-4`,
        isFetching && 'pointer-events-none animate-pulse opacity-60',
        className
      )}
      isLoading={isLoading}
    >
      <>
        <div className="flex flex-col justify-between gap-2 sm:flex-row">
          <h3 className="font-semibold text-nowrap">Energy management</h3>
          <div>
            <DateRangePicker
              onUpdate={(values) => {
                setDateRange((current) => ({
                  ...current,
                  startDate: values.range.from,
                  endDate: values.range.to,
                  rangeGranularity: values.rangeGranularity as RangeGranularity,
                }));
              }}
              initialDateFrom={dateRange.startDate}
              initialDateTo={dateRange.endDate}
              locale="ro-RO"
              defaultGranularity="5min"
              defaultMode="single"
              availableModes={['single', 'range']}
            />
          </div>
        </div>
        <div className="h-full">
          {data?.length ? (
            <EnergyManagementChart
              data={data}
              rangeGranularity={dateRange.rangeGranularity}
              hiddenMetrics={hiddenMetrics}
            />
          ) : isFetching ? (
            <div className="flex h-full min-h-[128px] w-full items-center justify-center rounded-md bg-gray-50"></div>
          ) : (
            <div className="flex h-full min-h-[128px] w-full items-center justify-center rounded-md bg-gray-50">
              <span className="text-muted-foreground">No data found</span>
            </div>
          )}
        </div>
      </>
    </DashboardCard>
  );
};
